/**
 * THE BROADCAST NAME OF A PLAYER ID. One join, one place.
 *
 * Owner, 2026-08-09: *"show the player name who has to do the throw in or player
 * name who won foul"* — *"and same for corner"*.
 *
 * ── Why this module had to exist at all ─────────────────────────────────────
 *
 * Before this, NO player name was drawn anywhere in the game. The squad screen
 * had names, and the commentary booth SPOKE names, but nothing on the pitch or
 * in the HUD ever wrote one: every banner said `#12`, and `#12` is a slot index,
 * not a person. The two halves of the join both existed and had simply never
 * been introduced —
 *
 *   - `render/player/castRegistry.ts assignCast()` resolves `playerId → CastSlot`
 *     (that IS the sim entity id ↔ cast character join, and its own header says
 *     so), but its only consumer used it to pick which GLB to draw;
 *   - `render/player/castManifest.ts castCharacter(id).name` holds the surname in
 *     caps — HAYES, REYNOLDS, OKONKWO — which is what goes on the back of a
 *     shirt and therefore what a broadcast caption says.
 *
 * ⚠ Note the two different name shapes, because picking the wrong one is the
 * easy mistake here. `CastSlot.name` (via `ui/teams.ts squadFor`) is the FULL
 * name in title case — "Hugo Okonkwo" — because a squad list wants a given name.
 * A caption wants the caps surname, so this module goes back to
 * `castCharacter(castId).name` for it and uses `CastSlot.name` only as a
 * fallback. `teams.ts`'s own comment explains the split.
 *
 * ── Naming rules this module inherits and must not break ────────────────────
 *
 * `ui/teams.ts` header, from a 2026-07-17 owner decision: NO real player names,
 * faces or club branding, and near-miss namealikes were REJECTED. Everything
 * here is read from the generated cast — coined surnames the art pipeline
 * authored — so it stays inside that ruling by construction. This module invents
 * no names of its own and must never gain a name table.
 *
 * ── Cost ───────────────────────────────────────────────────────────────────
 *
 * `assignCast` is deterministic and `squadFor` is memoised, so the assignment is
 * computed once per (squadSize, teamIds) and cached. Lookups are a Map hit. It
 * is safe to call `broadcastNameFor` from a per-frame path, but the callers here
 * are banners and a HUD line that repaint at 5–10 Hz.
 */

import { assignCast, type CastSlot } from '../render/player/castRegistry.js';
import { castCharacter } from '../render/player/castManifest.js';

interface NameRoster {
  readonly key: string;
  readonly byId: ReadonlyMap<number, CastSlot>;
}

let roster: NameRoster | null = null;

/**
 * Point the name lookup at this match's squads.
 *
 * Call once when a match starts. `playerId = team * squadSize + slot`
 * (sim/roster.ts), so the squad size is load-bearing: get it wrong and every
 * away player is named as somebody else. Re-calling with the same arguments is
 * free.
 */
export function configureMatchNames(squadSize: number, teamIds?: readonly [string, string]): void {
  const size = Math.max(1, squadSize | 0);
  const key = `${size}|${teamIds ? teamIds.join(',') : 'default'}`;
  if (roster && roster.key === key) return;
  const assignment = assignCast(teamIds ? { squadSize: size, teamIds } : { squadSize: size });
  roster = { key, byId: new Map(assignment.onPitch.map((s) => [s.playerId, s])) };
}

/** Drop the cached roster. For tests, and for leaving a match. */
export function resetMatchNames(): void {
  roster = null;
}

/**
 * The caps surname for a sim player id — "OKONKWO" — or `null` if this id has no
 * modelled character behind it.
 *
 * Returns null rather than a placeholder so a caller can decide whether an
 * unnamed player is worth a caption at all. `broadcastNameOr` is the version
 * that always yields something printable.
 */
export function broadcastNameFor(playerId: number): string | null {
  if (roster === null || playerId < 0) return null;
  const slot = roster.byId.get(playerId);
  if (slot === undefined) return null;
  if (slot.castId !== null) {
    const c = castCharacter(slot.castId);
    // The generated manifest already stores the surname in caps; upper-casing
    // again costs nothing and means a future manifest that stops doing so
    // cannot silently produce a lower-case caption.
    if (c) return c.name.toUpperCase();
  }
  // No model for this slot (a club with no built cast). `CastSlot.name` is the
  // full title-case name; take its last word so the caption shape stays the
  // same whichever branch produced it.
  if (slot.name && slot.name !== '—') {
    const parts = slot.name.trim().split(/\s+/);
    return parts[parts.length - 1]!.toUpperCase();
  }
  return null;
}

/**
 * A caption for `playerId` that is always printable: the surname when one is
 * known, otherwise the shirt-number form the banners used before names existed.
 *
 * The `#12` fallback is deliberate and must stay. It is how an unmodelled club
 * or a mis-sized squad degrades — to the OLD behaviour, which was at least
 * unambiguous — rather than to an empty caption that reads as a rendering bug.
 */
export function broadcastNameOr(playerId: number): string {
  if (playerId < 0) return '';
  return broadcastNameFor(playerId) ?? `#${playerId}`;
}

/** The shirt number for a player id, or -1. */
export function shirtNumberFor(playerId: number): number {
  if (roster === null || playerId < 0) return -1;
  const slot = roster.byId.get(playerId);
  return slot ? slot.number : -1;
}
